import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { getHttpErrorMessage, getHttpStatus } from '@/lib/httpErrors'
import type { MyBetSummary } from '@/types/miniApp'

/** Parlay V2 BLACK -- Personalized Parlay Lab (Sprint "Parlay V2 BLACK: Personalized Parlay
 * Lab", 05/09/2026). Los tipos reflejan EXACTAMENTE lo que devuelve `app/api/parlay_lab.py`/
 * `app/parlay_lab/service.py` -- nunca se fabrica un campo que el backend no envía. Mismo
 * patrón que `useJALab.ts` (misma arquitectura de sesiones/configuraciones para
 * herramientas personalizadas BLACK, ver Sección 5 del encargo). */

export type ParlayLabRiskProfile = 'conservador' | 'balanceado' | 'agresivo'
export type ParlayLabFamily = 'resultado' | 'goles' | 'corners' | 'tarjetas' | 'tiros' | 'jugadores'
export type ParlayLabStatus = 'valid' | 'no_parlay' | 'target_not_achievable' | 'informacion_insuficiente'

export interface ParlayLabAllowance {
  used: number
  limit: number | null
  remaining: number | null
  unlimited: boolean
  resets_at: string
}

export interface ParlayLabFixtureSummary {
  fixture_id: number
  competition_id: number | null
  competition_name: string | null
  competition_country: string | null
  competition_logo_url?: string | null
  home_team: string
  away_team: string
  home_logo: string | null
  away_logo: string | null
  kickoff_utc: string
  coverage_status: 'SUPPORTED' | 'LIMITED'
}

/** Odds Truth real (Secciones 2/4 del encargo, 24/09/2026) -- MISMA forma que JA Lab
 * (`JALabOddsSource`), nunca un bookmaker inventado. */
export interface ParlayLabOddsSource {
  bookmaker: string | null
  source: string | null
  fetched_at: string | null
  reference_text: string
}

export interface ParlayLabFamilyAvailability {
  family: ParlayLabFamily
  label: string
  available: boolean
}

export interface ParlayLabLeg {
  fixture_id: number
  market: string
  market_label: string
  family: ParlayLabFamily | null
  family_label: string | null
  selection: string
  selection_label: string
  home_team: string | null
  away_team: string | null
  home_logo?: string | null
  away_logo?: string | null
  competition_name?: string | null
  competition_logo_url?: string | null
  kickoff_utc: string | null
  // JA reference odds (cuota de MERCADO) -- INMUTABLE desde la generación.
  odds: number | null
  // Cuota JUSTA de John Anthony (1/model_probability) -- Sección 10 del encargo.
  fair_odds?: number | null
  model_probability: number | null
  confidence_score: number | null
  risk_score: number | null
  data_quality_score: number | null
  // Odds Truth real -- bookmaker observado + cuándo se capturó (Secciones 2/4 del encargo).
  odds_source?: ParlayLabOddsSource
}

export interface ParlayLabExplanation {
  why: string[]
  risks: string[]
  watch: string[]
}

export interface ParlayLabResult {
  id: number
  status: ParlayLabStatus
  fixture_ids: number[]
  requested_families: ParlayLabFamily[]
  risk_profile: ParlayLabRiskProfile
  target_odds_min: number | null
  target_odds_max: number | null
  legs: ParlayLabLeg[]
  combined_odds: number | null
  joint_probability_raw: number | null
  joint_probability_adjusted: number | null
  quality_score: number | null
  risk_score: number | null
  correlation_level: string | null
  correlation_reasoning: string | null
  explanation: ParlayLabExplanation
  failure_reason: string | null
  target_not_achievable_reason: string | null
  candidates_total: number
  combinations_evaluated: number
  generation_ms: number | null
  created_at: string
  allowance: ParlayLabAllowance | null
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/5 del encargo): "LA JUGUÉ" -- `my_bet` sigue
  // el MISMO patrón ya usado por picks/parlays oficiales/resultado exacto (`MyBetSummary`,
  // `_attach_my_bets`), nunca un mecanismo nuevo. `linked_personal_bet_id` es el enganche
  // crudo (`ParlayLabRun.linked_personal_bet_id`) -- el frontend siempre debe leer `my_bet`,
  // nunca este id directamente.
  linked_personal_bet_id?: number | null
  my_bet?: MyBetSummary | null
}

export function getParlayLabErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { message?: string } } } }).response
    const message = response?.data?.detail?.message
    if (message) return message
  }
  // Corrección real de usuario (05/09/2026): "No pudimos cargar esta información" NUNCA
  // debe tapar un timeout real -- si el navegador cortó la espera antes de que John Anthony
  // terminara de construir (poco frecuente ahora con `GENERATE_TIMEOUT_MS`, pero posible en
  // una red muy lenta), decirlo tal cual, nunca el copy genérico de "no cargó".
  if (error && typeof error === 'object' && (error as { code?: string }).code === 'ECONNABORTED') {
    return 'John Anthony está tardando más de lo esperado construyendo tu parlay. Espera un momento e inténtalo de nuevo -- si vuelve a pasar, revisa tu historial: la generación puede haberse completado igual.'
  }
  return getHttpErrorMessage(getHttpStatus(error))
}

export function getParlayLabErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { code?: string } } } }).response
    return response?.data?.detail?.code
  }
  return undefined
}

export function useParlayLabAllowance(enabled: boolean) {
  return useQuery<ParlayLabAllowance>({
    queryKey: ['parlay-lab', 'allowance'],
    queryFn: async () => (await apiClient.get('/api/mini-app/parlay-lab/allowance')).data,
    enabled,
    staleTime: 15_000,
  })
}

export function useParlayLabFixtureSearch(enabled: boolean, query: string) {
  return useQuery<{ fixtures: ParlayLabFixtureSummary[] }>({
    queryKey: ['parlay-lab', 'fixtures', query],
    queryFn: async () => (await apiClient.get('/api/mini-app/parlay-lab/fixtures', { params: query ? { q: query } : {} })).data,
    enabled,
    staleTime: 60_000,
  })
}

export function useParlayLabFamilies(enabled: boolean, fixtureIds: number[]) {
  return useQuery<{ families: ParlayLabFamilyAvailability[] }>({
    queryKey: ['parlay-lab', 'families', fixtureIds],
    queryFn: async () => (await apiClient.post('/api/mini-app/parlay-lab/families', { fixture_ids: fixtureIds })).data,
    enabled: enabled && fixtureIds.length > 0,
    staleTime: 60_000,
  })
}

export function useParlayLabHistory(enabled: boolean, offset = 0) {
  return useQuery<{ items: ParlayLabResult[] }>({
    queryKey: ['parlay-lab', 'history', offset],
    queryFn: async () => (await apiClient.get('/api/mini-app/parlay-lab/history', { params: { offset } })).data,
    enabled,
    staleTime: 15_000,
  })
}

export function useParlayLabRun(enabled: boolean, runId: number | null) {
  return useQuery<ParlayLabResult>({
    queryKey: ['parlay-lab', 'run', runId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/parlay-lab/runs/${runId}`)).data,
    enabled: enabled && runId != null,
  })
}

export interface GenerateParlayLabInput {
  fixtureIds: number[]
  families: ParlayLabFamily[]
  riskProfile: ParlayLabRiskProfile
  targetOddsMin?: number | null
  targetOddsMax?: number | null
  // Sección 6 del encargo (24/09/2026): "Eliminar selecciones correlacionadas" -- solo
  // endurece el umbral real, nunca relaja el bloqueo de HIGH.
  avoidCorrelation?: boolean
}

// Corrección real de usuario (05/09/2026): "se seleccionan >=3 partidos... termina en 'No
// pudimos cargar esta información'" -- causa exacta encontrada y REPRODUCIDA en producción:
// `apiClient` usa un timeout GLOBAL de 30s (`lib/apiClient.ts`), pero una generación real en
// frío con 3 fixtures REALES (exactamente el escenario reportado) midió 94.9s en producción
// (5 fixtures: 105.3s) -- nginx en el host ya toleraba 120s (`proxy_read_timeout`, ahora
// subido a 200s en el mismo cambio para dar margen real al máximo de 10 fixtures/petición),
// así que el corte SIEMPRE ocurría en el cliente, nunca en el proxy. Axios lanza un error SIN
// `.response` (ECONNABORTED) cuando su propio timeout expira -- `getParlayLabErrorMessage`/
// `getHttpStatus` no podían distinguirlo de un error de red genérico, de ahí el copy genérico
// "No pudimos cargar esta información" en vez de un mensaje real. Fix: un timeout POR
// PETICIÓN más alto (190s, con margen real bajo el nuevo techo de 200s de nginx) SOLO para
// este endpoint lento -- el resto de la app conserva el timeout global de 30s.
const GENERATE_TIMEOUT_MS = 190_000

export function useGenerateParlayLab() {
  const queryClient = useQueryClient()
  return useMutation<ParlayLabResult, unknown, GenerateParlayLabInput>({
    mutationFn: async ({ fixtureIds, families, riskProfile, targetOddsMin, targetOddsMax, avoidCorrelation }) =>
      (await apiClient.post(
        '/api/mini-app/parlay-lab/generate',
        {
          fixture_ids: fixtureIds, families, risk_profile: riskProfile,
          target_odds_min: targetOddsMin ?? null, target_odds_max: targetOddsMax ?? null,
          avoid_correlation: avoidCorrelation ?? false,
        },
        { timeout: GENERATE_TIMEOUT_MS }
      )).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parlay-lab', 'allowance'] })
      queryClient.invalidateQueries({ queryKey: ['parlay-lab', 'history'] })
    },
  })
}

// ---------------------------------------------------------------------------
// MODE B -- "CRÉALO TÚ MISMO": user picks specific legs, JA evaluates
// ---------------------------------------------------------------------------

export interface ParlayLabPick {
  fixture_id: number
  market: string
  market_label: string
  family: string | null
  family_label: string | null
  selection: string
  selection_label: string
  odds: number | null
  model_probability: number | null
  confidence_score: number
  risk_score: number
  data_quality_score: number
}

export function useParlayLabFixturePicks(enabled: boolean, fixtureId: number | null) {
  return useQuery<{ picks: ParlayLabPick[] }>({
    queryKey: ['parlay-lab', 'picks', fixtureId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/parlay-lab/fixtures/${fixtureId}/picks`)).data,
    enabled: enabled && fixtureId != null,
    staleTime: 30_000,
  })
}

export interface EvaluateLegInput {
  fixture_id: number
  market: string
  selection: string
}

/** Sprint "Parlay Lab: Cierre funcional + UX Premium" (24/09/2026, Sección 5A del encargo):
 * BUG REAL corregido -- `/evaluate` ya persiste (mismo `ParlayLabRun` que `/generate`), así
 * que devuelve el MISMO `ParlayLabResult` real (con `id`/`allowance`/`my_bet`) -- nunca un
 * tipo "ligero" distinto que no pudiera continuar hacia detalle/guardar/"La jugué". */
export function useEvaluateParlayLab() {
  const queryClient = useQueryClient()
  return useMutation<ParlayLabResult, unknown, { legs: EvaluateLegInput[] }>({
    mutationFn: async ({ legs }) =>
      (await apiClient.post('/api/mini-app/parlay-lab/evaluate', { legs }, { timeout: GENERATE_TIMEOUT_MS })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parlay-lab', 'allowance'] })
      queryClient.invalidateQueries({ queryKey: ['parlay-lab', 'history'] })
    },
  })
}
